import { api } from '@/lib/api';
import type {
  Account,
  AccountDashboardData,
  AccountHealth,
  AccountMetrics,
  AccountSubscription,
  AccountActivity,
  SubscriptionDetail,
  InvoiceDetail,
  TransmitterDetail,
  QuoteDetail,
  TicketDetail,
  SiteDetail,
  ActivityDetail,
  CallDetail,
  AccountCommunication,
  AccountCommunicationsSummary,
  UserDetail,
  SensorDetail,
  UsergroupMemberDetail,
  PaginatedResponse,
  OpportunityDetail,
  OpportunitiesResponse,
  CreateOpportunityInput,
  PipelineSummary,
  DealsNeedingAttention,
  QmsTicketSummary,
  QmsTicketStatus,
  AccountTableRow,
  AccountTableSortKey,
  AccountNote,
  CreateAccountInput,
  Organisation,
  LegacyHardwareOption,
  LegacyOrganisationConfig,
  V4QuoteFormDetail,
  AccountOrganisation,
  AvailableUsergroupsOrg,
} from './types';

// =============================================================================
// V4 API Response Types
// =============================================================================

interface V4AccountsResponse {
  accounts: V4Account[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

interface V4AccountResponse {
  account: V4Account;
}

interface V4OrganisationResponse {
  organisation: {
    id: string;
    mongoId: string;
    name: string;
    accountNumber?: string | null;
    tags?: string[] | null;
    description?: string | null;
    sensorHardwares?: string[] | null;
    connectivities?: string[] | null;
    defaultConfigTags?: string[] | null;
    featureFlags?: Record<string, unknown> | null;
  };
}

/**
 * V4 Account from API
 * Note: getMany returns a subset of fields, getOneByID returns all fields
 */
interface V4Account {
  id: string;
  accountNumber: string;
  name: string;
  type: string;
  status: string;
  tier: string | null;
  salesManagerId?: string | null;
  salesManagerName?: string | null;
  currency?: string;                    // Returned by getMany
  hubspotCompanyId?: string | null;     // Returned by getMany
  tags?: string[];                      // Returned by getMany
  createdAt: string;
  updatedAt: string;
  // Fields only returned by getOneByID (full record)
  description?: string | null;
  prospectDate?: string | null;
  firstSubscriptionDate?: string | null;
  /** @deprecated Use firstSubscriptionDate */
  firstContractDate?: string | null;
  firstRevenueDate?: string | null;
  churnDate?: string | null;
  reactivationDate?: string | null;
  language?: string;
  timezone?: string;
  quickbooksCustomerId?: string | null;
  gdriveRootFolderId?: string | null;
  alertVoiceProvider?: 'twilio' | 'telnyx';

  // Billing / AR routing (migration 0246)
  billingSiteId?: string | null;
  billingAddress?: string | null;
  paymentTerms?: string | null;
  paymentMethod?: string | null;
  apContactEmails?: string[] | null;

  enabled?: boolean;
  hasCalibrationSubscription?: string | null;
  /** @deprecated Use hasCalibrationSubscription */
  hasCalibrationContract?: string | null;
  hasOnsiteCalibration?: string | null;
  calibrationProvider?: string | null;
  requiresWhitelisting?: string | null;
  mongoOrganisationId?: string | null;
  organisationId?: string | null;
  organisationName?: string | null;
  organisationNumber?: string | null;
  createdBy?: string | null;
  archivedAt?: string | null;
  archivedBy?: string | null;
  archivedByName?: string | null;
  deletedAt?: string | null;
}

interface V4DashboardResponse {
  account: {
    id: string;
    accountNumber: string;
    name: string;
    mongoOrganisationId?: string | null;
    organisationId?: string | null;
    type: string;
    status: string;
    tier: string | null;
    firstSubscriptionDate?: string | null;
    /** @deprecated Use firstSubscriptionDate */
    firstContractDate?: string | null;
    hasCalibrationSubscription?: string | null;
    /** @deprecated Use hasCalibrationSubscription */
    hasCalibrationContract?: string | null;
    hasOnsiteCalibration?: string | null;
    calibrationProvider?: string | null;
    requiresWhitelisting?: string | null;
    salesManagerId?: string | null;
    salesManagerName?: string | null;
    archivedAt?: string | null;
    archivedBy?: string | null;
    archivedByName?: string | null;
  };
  health: {
    score: number | null;
    subscriptionHealthScore: number | null;
    /** @deprecated Use subscriptionHealthScore */
    contractHealthScore?: number | null;
    paymentHealthScore: number | null;
    engagementHealthScore: number | null;
    supportHealthScore: number | null;
    trend: string | null;
  };
  metrics: {
    arr: string | null;
    mrr: string | null;
    activeSensors: number;
    inactiveSensors: number;
    maintenanceSensors?: number;
    inAlarmSensors?: number;
    toAcknowledgeSensors?: number;
    inhibitedSensors?: number;
    unmanagedSensors?: number;
    archivedSensors?: number;
    totalSensors?: number;
    totalSites: number;
    openTickets: number;
    criticalTickets: number;
    totalGateways: number;
  };
  subscription: {
    current: V4Subscription | null;
    summary: {
      total: number;
      active: number;
      expiringSoon: number;
    };
    history: Array<{
      id: string;
      subscriptionNumber: string;
      name: string;
      startDate: string;
      endDate: string;
      totalValue: string;
    }>;
    hasRenewalOpportunity: boolean;
  };
  invoices: {
    total: number;
    preparation: number;
    sent: number;
    paid: number;
    overdue: number;
    totalPaid: string;
    totalOutstanding: string;
  };
  quotes: {
    total: number;
    preparation: number;
    sent: number;
    approved: number;
    invoiced: number;
    // Values by status for Quote Pipeline Card
    preparationValue: string;
    sentValue: string;
    approvedValue: string;
    invoicedValue: string;
    totalValue: string;
  };
  sites: {
    total: number;
    active: number;
  };
  recentActivity: Array<{
    id: string;
    type: string;           // Note: DAO returns 'type', not 'activityType'
    description: string;
    timestamp: string;      // Note: DAO returns 'timestamp', not 'createdAt'
    entityType?: string;
    entityId?: string;
  }>;
  activitySummary: {
    activityCount30Days: number;
    lastActivityAt: string | null;
    lastActivityType: string | null;
  };
  alerts: Array<{
    type: string;
    message: string;
    severity: string;       // Note: DAO returns 'error' for critical, 'warning' for warning
  }>;
}

interface V4Subscription {
  id: string;
  subscriptionNumber: string;
  name: string;
  status?: string;          // Optional - not always returned from dashboard
  startDate?: string;       // Optional - not always returned from dashboard
  endDate: string;
  totalValue: string;
  currency?: string;        // Optional - not always returned from dashboard
  daysRemaining?: number;   // Extra field from dashboard endpoint
  salesManagerId?: string;
  salesManagerName?: string;
  contractualManagerIds?: string[];
  contractualManagerNames?: (string | null)[];
  mongoContractId?: string | null; // MongoDB ObjectId for quotes/invoices that still use MongoDB
}

// =============================================================================
// API Functions
// =============================================================================

export async function getAccounts(params?: {
  pageNumber?: number;
  pageLength?: number;
  status?: string;
  type?: string;
  types?: string;
  search?: string;
}): Promise<{ accounts: Account[]; total: number }> {
  const response = await api.get<V4AccountsResponse>('/v4/accounts', params);

  return {
    accounts: response.accounts.map(transformV4Account),
    total: response.total,
  };
}

// =============================================================================
// V4 Table View API Response Types
// =============================================================================

interface V4AccountTableRow {
  id: string;
  accountNumber: string;
  name: string;
  status: string;
  tier: string | null;
  currency: string | null;
  hasCalibrationSubscription: string | null;
  hasOnsiteCalibration: string | null;
  requiresWhitelisting: string | null;
  // Enriched fields
  contractualManagerName: string | null;
  technicalManagerName?: string | null;
  itManagerName?: string | null;
  contractualManagerContactId?: string | null;
  technicalManagerContactId?: string | null;
  itManagerContactId?: string | null;
  salesManagerId: string | null;
  salesManagerName: string | null;
  subscriptionId: string | null;
  subscriptionNumber: string | null;
  subscriptionStartDate: string | null;
  subscriptionEndDate: string | null;
  subscriptionYears: number | null;
  sensorsQty: number;
  calibrationsDue: number;
  overdueCalibrations: number;
  arr: number | null;
  // Operational metrics (from account_health_metrics)
  activeSensorsCount?: number;
  inactiveSensorsCount?: number;
  maintenanceSensorsCount?: number;
  inAlarmSensorsCount?: number;
  unmanagedSensorsCount?: number;
  inhibitedSensorsCount?: number;
  totalSensorsCount?: number;
  activeTransmittersCount?: number;
  inactiveTransmittersCount?: number;
  gatewaysCount?: number;
  simCardsCount?: number;
  usergroupsCount?: number;
  usersCount?: number;
  openTicketsCount?: number;
  alert24hCount?: number;
  emails24hCount?: number;
  calls24hCount?: number;
  sensorsInAlarmCount?: number;
  lowBatteryDevicesCount?: number;
  daysUntilSubscriptionExpiry?: number | null;
  totalInvoicesCount?: number;
  ltv?: number;
  // Audit / configuration completeness flags
  hasContractualManager?: boolean;
  hasSalesManager?: boolean;
  hasDriveFolder?: boolean;
  hasQbCustomerId?: boolean;
  hasHubspotCompanyId?: boolean;
  // Raw IDs for Drive / HubSpot / QB clickable links
  gdriveRootFolderId?: string | null;
  hubspotCompanyId?: string | null;
  quickbooksCustomerId?: string | null;
}

interface V4AccountsTableResponse {
  accounts: V4AccountTableRow[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

/**
 * Get accounts with enriched data for table view
 * Includes: contractual_manager, contract info, sensors qty, calibrations due,
 * overdue calibrations, ARR
 */
export async function getAccountsTable(params?: {
  pageNumber?: number;
  pageLength?: number;
  status?: string;
  type?: string;
  search?: string;
  accountNumberSearch?: string;
  nameSearch?: string;
  contractualManagerSearch?: string;
  salesManagerSearch?: string;
  contractNumberSearch?: string;
  salesManagerId?: string;
  hasCalibrationSubscription?: string;
  hasOnsiteCalibration?: string;
  excludeOrganizations?: string;
  sortKey?: AccountTableSortKey;
  sortDirection?: 1 | -1;
}): Promise<{ accounts: AccountTableRow[]; total: number; pageNumber: number; pageLength: number }> {
  const response = await api.get<V4AccountsTableResponse>('/v4/accounts', {
    ...params,
    view: 'table',
  });

  return {
    accounts: response.accounts.map(transformV4AccountTableRow),
    total: response.total,
    pageNumber: response.pageNumber,
    pageLength: response.pageLength,
  };
}

// =============================================================================
// Sales Managers
// =============================================================================

export interface SalesManager {
  id: string;
  mongoUserId?: string | null;
  name: string;
  isFormer?: boolean;
}

const ACCOUNT_SYNC_POLL_INTERVAL_MS = 500;
const ACCOUNT_SYNC_POLL_ATTEMPTS = 20;

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function normalizeFeatureFlags(flags: Record<string, unknown> | null | undefined): Record<string, boolean> {
  if (!flags || typeof flags !== 'object') return {};

  return Object.fromEntries(
    Object.entries(flags).map(([key, value]) => [key, Boolean(value)])
  );
}

function transformSyncedOrganisationConfig(
  organisation: V4OrganisationResponse['organisation']
): LegacyOrganisationConfig {
  return {
    _id: organisation.mongoId,
    name: organisation.name,
    tags: Array.isArray(organisation.tags) ? organisation.tags.filter(Boolean) : [],
    sensor_hardwares: Array.isArray(organisation.sensorHardwares)
      ? organisation.sensorHardwares.filter(Boolean)
      : [],
    connectivities: Array.isArray(organisation.connectivities)
      ? organisation.connectivities.filter(Boolean)
      : [],
    description: organisation.description || '',
    default_config_tags: Array.isArray(organisation.defaultConfigTags)
      ? organisation.defaultConfigTags.filter(Boolean)
      : [],
    featureFlags: normalizeFeatureFlags(organisation.featureFlags),
  };
}

function normalizeLegacyConfigForComparison(config: LegacyOrganisationConfig) {
  return {
    sensor_hardwares: [...config.sensor_hardwares].sort(),
    connectivities: [...config.connectivities].sort(),
    description: config.description || '',
    default_config_tags: [...config.default_config_tags].sort(),
    featureFlags: Object.fromEntries(
      Object.entries(config.featureFlags).sort(([left], [right]) => left.localeCompare(right))
    ),
  };
}

function legacyOrganisationConfigMatches(
  actual: LegacyOrganisationConfig,
  expected: Pick<
    LegacyOrganisationConfig,
    'sensor_hardwares' | 'connectivities' | 'description' | 'default_config_tags' | 'featureFlags'
  >
) {
  return JSON.stringify(normalizeLegacyConfigForComparison(actual)) === JSON.stringify(
    normalizeLegacyConfigForComparison({
      _id: actual._id,
      name: actual.name,
      tags: actual.tags,
      sensor_hardwares: expected.sensor_hardwares,
      connectivities: expected.connectivities,
      description: expected.description,
      default_config_tags: expected.default_config_tags,
      featureFlags: expected.featureFlags,
    })
  );
}

type CreateAccountCreatedResponse = {
  account: { id: string };
  mongoOrganisationId?: string;
};

type CreateAccountPendingResponse = {
  status: 'pending';
  mongoOrganisationId: string;
  pollToken?: string;
  message?: string;
};

export type CreateAccountResult =
  | { status: 'created'; id: string }
  | { status: 'pending'; mongoOrganisationId: string; pollToken?: string; message?: string };

/**
 * Get distinct sales managers from contracts, quotes, and invoices
 * For use in filter dropdowns
 */
export async function getSalesManagers(): Promise<SalesManager[]> {
  const response = await api.get<{ salesManagers: SalesManager[] }>('/v4/accounts/sales-managers');
  return response.salesManagers;
}

/**
 * Search organisations by name
 * For use in organisation assignment dropdowns. Defaults to customer organisations
 * — supplier orgs must never appear in customer-account assignment dropdowns.
 */
export async function searchOrganisations(
  query: string,
  options: { type?: 'customer' | 'supplier' | null } = {},
): Promise<Organisation[]> {
  const { type = 'customer' } = options;
  const params: Record<string, string | number> = {
    search: query,
    pageLength: 20,
  };
  if (type) params.type = type;
  const response = await api.get<{ organisations: Organisation[] }>('/v4/organisations', params);
  return response.organisations;
}

export async function getLegacyHardwareOptions(
  type: 'module' | 'connectivity'
): Promise<LegacyHardwareOption[]> {
  const response = await api.get<{ hardwares: LegacyHardwareOption[] }>('/v4/hardwares', {
    type,
    pageLength: 100,
    sortKey: 'name',
  });

  return response.hardwares ?? [];
}

export async function getOrganisationConfigFromPostgres(
  mongoOrganisationId: string
): Promise<LegacyOrganisationConfig> {
  const response = await api.get<V4OrganisationResponse>(`/v4/organisations/${mongoOrganisationId}`);
  return transformSyncedOrganisationConfig(response.organisation);
}

/**
 * Create a new account
 */
export async function createAccount(data: CreateAccountInput): Promise<CreateAccountResult> {
  const response = await api.post<CreateAccountCreatedResponse | CreateAccountPendingResponse>(
    '/v4/accounts',
    data
  );

  if ('account' in response) {
    return {
      status: 'created',
      id: response.account.id,
    };
  }

  if (response.status === 'pending') {
    return {
      status: 'pending',
      mongoOrganisationId: response.mongoOrganisationId,
      pollToken: response.pollToken,
      message: response.message,
    };
  }

  throw new Error('Unexpected account creation response');
}

/**
 * Resolve a PostgreSQL account created from a MongoDB organisation.
 */
export async function getAccountByMongoOrganisationId(
  mongoOrganisationId: string,
  pollToken?: string
): Promise<CreateAccountResult> {
  const response = await api.get<CreateAccountCreatedResponse | CreateAccountPendingResponse>(
    `/v4/accounts/by-mongo-org/${mongoOrganisationId}`,
    pollToken ? { pollToken } : undefined
  );

  if ('account' in response) {
    return {
      status: 'created',
      id: response.account.id,
    };
  }

  if (response.status === 'pending') {
    return {
      status: 'pending',
      mongoOrganisationId: response.mongoOrganisationId,
      pollToken: response.pollToken,
      message: response.message,
    };
  }

  throw new Error('Unexpected account resolution response');
}

/**
 * Search users for contractual manager picker
 */
export async function searchAccountUsers(
  accountId: string,
  query: string
): Promise<{ id: string; name: string }[]> {
  const response = await api.get<{ users: { id: string; name: string }[] }>(
    `/v4/accounts/${accountId}/users/search`,
    { q: query }
  );
  return response.users;
}

/**
 * Update subscription manager assignments
 */
export async function updateContractManagers(
  accountId: string,
  subscriptionId: string,
  data: { salesManagerId?: string; contractualManagerIds?: string[] }
): Promise<void> {
  await api.patch(`/v4/accounts/${accountId}/subscriptions/${subscriptionId}`, data);
}

/**
 * Get all enabled usergroup members for picker dropdown
 */
export async function getAccountMembersForPicker(
  accountId: string
): Promise<UsergroupMemberDetail[]> {
  const response = await api.get<V4UsergroupMembersResponse>(
    `/v4/accounts/${accountId}/usergroup-members`,
    { enabled: 'true', pageLength: 100 }
  );
  // Deduplicate by mongoUserId (same person can appear in multiple usergroups).
  // Prefer the entry that has tags (e.g. "Contractual Manager") to preserve selection state.
  const byUser = new Map<string, UsergroupMemberDetail>();
  for (const m of response.members || []) {
    const existing = byUser.get(m.mongoUserId);
    if (!existing || (m.tags && m.tags.length > 0 && (!existing.tags || existing.tags.length === 0))) {
      byUser.set(m.mongoUserId, m);
    }
  }
  return Array.from(byUser.values());
}

/**
 * Canonical read for CM / TM / IT contacts on an account.
 * GET /v4/accounts/:id/contact-roles — reads contact_roles JOIN contacts.
 */
export interface ContactRoleEntry {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  mobile_phone: string | null;
  title: string | null;
  ordinal: number;
}

export interface AccountContactRoles {
  contractual_manager: ContactRoleEntry[];
  technical_manager: ContactRoleEntry[];
  it_manager: ContactRoleEntry[];
  billing_manager: ContactRoleEntry[];
}

export async function getAccountContactRoles(accountId: string): Promise<AccountContactRoles> {
  return api.get<AccountContactRoles>(`/v4/accounts/${accountId}/contact-roles`);
}

export interface InvoiceReminderContact {
  name: string;
  phone: string | null;
  email: string | null;
}

export interface AccountInvoiceReminderState {
  reminder: { overdueSince: string; updatedAt: string; updatedBy: string | null } | null;
  contact: InvoiceReminderContact | null;
  coverage: { usergroups: { id: string; name: string }[]; organisationIds: string[] };
}

export async function getAccountInvoiceReminder(accountId: string): Promise<AccountInvoiceReminderState> {
  return api.get<AccountInvoiceReminderState>(`/v4/invoice-reminders/accounts/${accountId}`);
}

export async function setAccountInvoiceReminder(accountId: string, overdueSince: string): Promise<AccountInvoiceReminderState> {
  return api.put<AccountInvoiceReminderState>(`/v4/invoice-reminders/accounts/${accountId}`, { overdueSince });
}

export async function clearAccountInvoiceReminder(accountId: string): Promise<void> {
  await api.delete<void>(`/v4/invoice-reminders/accounts/${accountId}`);
}

/**
 * Update member tags for an account (set tag on specific members, remove from others)
 */
export async function updateMemberTags(
  accountId: string,
  tag: string,
  memberIds: string[]
): Promise<void> {
  await api.patch(`/v4/accounts/${accountId}/usergroup-members/tags`, { tag, memberIds });
}

/**
 * Transform V4 enriched account row to internal format
 */
function transformV4AccountTableRow(v4: V4AccountTableRow): AccountTableRow {
  return {
    id: v4.id,
    account_number: v4.accountNumber,
    name: v4.name,
    status: v4.status as AccountTableRow['status'],
    tier: (v4.tier as AccountTableRow['tier']) || 'standard',
    currency: v4.currency || undefined,
    has_calibration_subscription: (v4.hasCalibrationSubscription as AccountTableRow['has_calibration_subscription']) || 'false',
    has_calibration_contract: (v4.hasCalibrationSubscription as AccountTableRow['has_calibration_contract']) || 'false',
    has_onsite_calibration: (v4.hasOnsiteCalibration as AccountTableRow['has_onsite_calibration']) || 'false',
    requires_whitelisting: (v4.requiresWhitelisting as AccountTableRow['requires_whitelisting']) || 'false',
    // Enriched fields
    contractual_manager_name: v4.contractualManagerName,
    technical_manager_name: v4.technicalManagerName ?? null,
    it_manager_name: v4.itManagerName ?? null,
    contractual_manager_contact_id: v4.contractualManagerContactId ?? null,
    technical_manager_contact_id: v4.technicalManagerContactId ?? null,
    it_manager_contact_id: v4.itManagerContactId ?? null,
    sales_manager_id: v4.salesManagerId,
    sales_manager_name: v4.salesManagerName,
    contract_id: v4.subscriptionId,
    contract_number: v4.subscriptionNumber,
    contract_start_date: v4.subscriptionStartDate,
    contract_end_date: v4.subscriptionEndDate,
    contract_years: v4.subscriptionYears,
    sensors_qty: v4.sensorsQty,
    calibrations_due: v4.calibrationsDue,
    overdue_calibrations: v4.overdueCalibrations,
    arr: v4.arr,
    // Operational metrics
    active_sensors_count: v4.activeSensorsCount ?? 0,
    inactive_sensors_count: v4.inactiveSensorsCount ?? 0,
    maintenance_sensors_count: v4.maintenanceSensorsCount ?? 0,
    in_alarm_sensors_count: v4.inAlarmSensorsCount ?? 0,
    unmanaged_sensors_count: v4.unmanagedSensorsCount ?? 0,
    inhibited_sensors_count: v4.inhibitedSensorsCount ?? 0,
    total_sensors_count: v4.totalSensorsCount ?? 0,
    active_transmitters_count: v4.activeTransmittersCount ?? 0,
    inactive_transmitters_count: v4.inactiveTransmittersCount ?? 0,
    gateways_count: v4.gatewaysCount ?? 0,
    sim_cards_count: v4.simCardsCount ?? 0,
    usergroups_count: v4.usergroupsCount ?? 0,
    users_count: v4.usersCount ?? 0,
    open_tickets_count: v4.openTicketsCount ?? 0,
    alert_24h_count: v4.alert24hCount ?? 0,
    emails_24h_count: v4.emails24hCount ?? 0,
    calls_24h_count: v4.calls24hCount ?? 0,
    sensors_in_alarm_count: v4.sensorsInAlarmCount ?? 0,
    low_battery_devices_count: v4.lowBatteryDevicesCount ?? 0,
    days_until_subscription_expiry: v4.daysUntilSubscriptionExpiry ?? null,
    total_invoices_count: v4.totalInvoicesCount ?? 0,
    ltv: v4.ltv ?? 0,
    // Audit flags
    has_contractual_manager: v4.hasContractualManager ?? false,
    has_sales_manager: v4.hasSalesManager ?? false,
    has_drive_folder: v4.hasDriveFolder ?? false,
    has_qb_customer_id: v4.hasQbCustomerId ?? false,
    has_hubspot_company_id: v4.hasHubspotCompanyId ?? false,
    gdrive_root_folder_id: v4.gdriveRootFolderId ?? null,
    hubspot_company_id: v4.hubspotCompanyId ?? null,
    quickbooks_customer_id: v4.quickbooksCustomerId ?? null,
  };
}

export async function getAccount(accountId: string, options?: { includeArchived?: boolean }): Promise<Account> {
  const response = await api.get<V4AccountResponse>(`/v4/accounts/${accountId}`, options?.includeArchived ? { includeArchived: 'true' } : undefined);
  return transformV4Account(response.account);
}

export async function archiveAccount(accountId: string): Promise<Account> {
  const response = await api.post<V4AccountResponse>(`/v4/accounts/${accountId}/archive`);
  return transformV4Account(response.account);
}

export async function unarchiveAccount(accountId: string): Promise<Account> {
  const response = await api.post<V4AccountResponse>(`/v4/accounts/${accountId}/unarchive`);
  return transformV4Account(response.account);
}

/**
 * Update account status (suspend/activate)
 */
export async function updateAccountStatus(
  accountId: string,
  status: 'active' | 'suspended'
): Promise<Account> {
  const response = await api.patch<V4AccountResponse>(`/v4/accounts/${accountId}`, { status });
  return transformV4Account(response.account);
}

/**
 * Update account calibration contract flag
 */
export async function updateAccountCalibrationContract(
  accountId: string,
  value: 'true' | 'false' | 'other'
): Promise<Account> {
  const response = await api.patch<V4AccountResponse>(`/v4/accounts/${accountId}`, {
    hasCalibrationSubscription: value,
  });
  return transformV4Account(response.account);
}

/**
 * Update account onsite calibration flag
 */
export async function updateAccountOnsiteCalibration(
  accountId: string,
  value: 'true' | 'false' | 'other'
): Promise<Account> {
  const response = await api.patch<V4AccountResponse>(`/v4/accounts/${accountId}`, {
    hasOnsiteCalibration: value,
  });
  return transformV4Account(response.account);
}

/**
 * Update account calibration provider (who calibrates: ATEK / client / to-verify)
 */
export async function updateAccountCalibrationProvider(
  accountId: string,
  value: 'atek' | 'client' | 'other'
): Promise<Account> {
  const response = await api.patch<V4AccountResponse>(`/v4/accounts/${accountId}`, {
    calibrationProvider: value,
  });
  return transformV4Account(response.account);
}

/**
 * Update account whitelisting (decloisonnement) flag
 */
export async function updateAccountWhitelisting(
  accountId: string,
  value: 'true' | 'false' | 'other'
): Promise<Account> {
  const response = await api.patch<V4AccountResponse>(`/v4/accounts/${accountId}`, {
    requiresWhitelisting: value,
  });
  return transformV4Account(response.account);
}

/**
 * Update account name
 */
export async function updateAccountName(
  accountId: string,
  mongoOrganisationId: string,
  name: string
): Promise<{ account: Account | null; synced: boolean }> {
  await api.put(`/v4/legacy/v2/superadmin/organisations/${mongoOrganisationId}`, { name });

  for (let attempt = 0; attempt < ACCOUNT_SYNC_POLL_ATTEMPTS; attempt += 1) {
    await sleep(ACCOUNT_SYNC_POLL_INTERVAL_MS);

    const account = await getAccount(accountId);
    if (account.name === name) {
      return { account, synced: true };
    }
  }

  return { account: null, synced: false };
}

export async function updateLegacyOrganisationConfig(
  mongoOrganisationId: string,
  data: Pick<
    LegacyOrganisationConfig,
    'sensor_hardwares' | 'connectivities' | 'description' | 'default_config_tags' | 'featureFlags'
  >
): Promise<{ organisation: LegacyOrganisationConfig | null; synced: boolean }> {
  await api.put(`/v4/legacy/v2/superadmin/organisations/${mongoOrganisationId}`, data);

  for (let attempt = 0; attempt < ACCOUNT_SYNC_POLL_ATTEMPTS; attempt += 1) {
    await sleep(ACCOUNT_SYNC_POLL_INTERVAL_MS);

    try {
      const organisation = await getOrganisationConfigFromPostgres(mongoOrganisationId);
      if (legacyOrganisationConfigMatches(organisation, data)) {
        return { organisation, synced: true };
      }
    } catch {
      // Ignore transient sync/read errors while CDC catches up.
    }
  }

  return { organisation: null, synced: false };
}

/**
 * Update account sales manager (Account Manager)
 */
/**
 * Update account settings (external IDs and links)
 */
export async function updateAccountSettings(
  accountId: string,
  settings: {
    hubspotCompanyId?: string | null;
    quickbooksCustomerId?: string | null;
    gdriveRootFolderId?: string | null;
    organisationId?: string | null;
    billingSiteId?: string | null;
    billingAddress?: string | null;
    paymentTerms?: string | null;
    paymentMethod?: string | null;
    apContactEmails?: string[];
    alertVoiceProvider?: 'twilio' | 'telnyx';
  }
): Promise<Account> {
  const response = await api.patch<V4AccountResponse>(`/v4/accounts/${accountId}`, settings);
  return transformV4Account(response.account);
}

export async function updateAccountSalesManager(
  accountId: string,
  salesManagerId: string | null
): Promise<{ account: Account | null; synced: boolean }> {
  await api.patch(`/v4/accounts/${accountId}/sales-manager`, {
    salesManagerId: salesManagerId || null,
  });
  const account = await getAccount(accountId);
  return { account, synced: true };
}

export async function getAccountDashboard(
  accountId: string,
  options?: { includeArchived?: boolean },
): Promise<AccountDashboardData> {
  const response = await api.get<V4DashboardResponse>(
    `/v4/accounts/${accountId}/dashboard`,
    options?.includeArchived ? { includeArchived: 'true' } : undefined,
  );

  return transformV4Dashboard(response);
}

// =============================================================================
// Google Drive folder listing (read-only)
// =============================================================================

export type AccountDriveStatus = 'ok' | 'not_configured' | 'forbidden' | 'not_found';

export interface DriveFileOwner {
  displayName?: string;
  emailAddress?: string;
}

export interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  iconLink?: string;
  webViewLink?: string;
  modifiedTime?: string;
  size?: string;
  owners?: DriveFileOwner[];
}

export interface AccountDriveFilesResponse {
  status: AccountDriveStatus;
  folderId: string | null;
  folderUrl: string | null;
  serviceAccountEmail: string | null;
  files: DriveFile[];
  message?: string;
}

export async function getAccountDriveFiles(
  accountId: string,
  folderId?: string,
): Promise<AccountDriveFilesResponse> {
  return api.get<AccountDriveFilesResponse>(
    `/v4/accounts/${accountId}/drive-files`,
    folderId ? { folderId } : undefined,
  );
}

// =============================================================================
// Transform Functions
// =============================================================================

function transformV4Account(v4: V4Account): Account {
  return {
    id: v4.id,
    account_number: v4.accountNumber,
    name: v4.name,
    mongo_organisation_id: v4.mongoOrganisationId || undefined,
    type: v4.type as Account['type'],
    status: v4.status as Account['status'],
    tier: (v4.tier as Account['tier']) || 'standard',
    prospect_date: v4.prospectDate || undefined,
    first_subscription_date: v4.firstSubscriptionDate || v4.firstContractDate || undefined,
    first_contract_date: v4.firstSubscriptionDate || v4.firstContractDate || undefined,
    first_revenue_date: v4.firstRevenueDate || undefined,
    churn_date: v4.churnDate || undefined,
    has_calibration_subscription: ((v4.hasCalibrationSubscription || v4.hasCalibrationContract || 'false') as Account['has_calibration_subscription']),
    has_calibration_contract: ((v4.hasCalibrationSubscription || v4.hasCalibrationContract || 'false') as Account['has_calibration_contract']),
    has_onsite_calibration: ((v4.hasOnsiteCalibration || 'false') as Account['has_onsite_calibration']),
    calibration_provider: ((v4.calibrationProvider || 'other') as Account['calibration_provider']),
    requires_whitelisting: (v4.requiresWhitelisting as Account['requires_whitelisting']) || 'false',
    sales_manager_id: v4.salesManagerId || null,
    sales_manager_name: v4.salesManagerName || null,
    organisation_id: v4.organisationId || undefined,
    organisation_name: v4.organisationName || undefined,
    organisation_number: v4.organisationNumber || undefined,
    hubspot_company_id: v4.hubspotCompanyId || undefined,
    quickbooks_customer_id: v4.quickbooksCustomerId || undefined,
    gdrive_root_folder_id: v4.gdriveRootFolderId || undefined,
    alert_voice_provider: v4.alertVoiceProvider === 'telnyx' ? 'telnyx' : 'twilio',
    billing_site_id: v4.billingSiteId ?? null,
    billing_address: v4.billingAddress ?? null,
    payment_terms: v4.paymentTerms ?? null,
    payment_method: v4.paymentMethod ?? null,
    ap_contact_emails: v4.apContactEmails ?? [],
    archived_at: v4.archivedAt || null,
    archived_by: v4.archivedBy || null,
    archived_by_name: v4.archivedByName || null,
    created_at: v4.createdAt,
    updated_at: v4.updatedAt,
  };
}

function transformV4Dashboard(v4: V4DashboardResponse): AccountDashboardData {
  // Transform health data
  const health: AccountHealth = {
    score: v4.health.score ?? null,
    trend: (v4.health.trend as AccountHealth['trend']) || 'stable',
    factors: {
      subscription_status: getHealthFactor(v4.health.subscriptionHealthScore ?? v4.health.contractHealthScore ?? null),
      contract_status: getHealthFactor(v4.health.subscriptionHealthScore ?? v4.health.contractHealthScore ?? null),
      payment_history: getPaymentFactor(v4.health.paymentHealthScore),
      engagement: getHealthFactor(v4.health.engagementHealthScore),
      support_tickets: getHealthFactor(v4.health.supportHealthScore),
    },
  };

  // Transform metrics
  const metrics: AccountMetrics = {
    arr: parseFloat(v4.metrics.arr || '0'),
    mrr: parseFloat(v4.metrics.mrr || '0'),
    active_sensors: v4.metrics.activeSensors,
    inactive_sensors: v4.metrics.inactiveSensors,
    maintenance_sensors: v4.metrics.maintenanceSensors ?? 0,
    in_alarm_sensors: v4.metrics.inAlarmSensors ?? 0,
    to_acknowledge_sensors: v4.metrics.toAcknowledgeSensors ?? 0,
    inhibited_sensors: v4.metrics.inhibitedSensors ?? 0,
    unmanaged_sensors: v4.metrics.unmanagedSensors ?? 0,
    archived_sensors: v4.metrics.archivedSensors ?? 0,
    total_sensors: v4.metrics.totalSensors ?? (v4.metrics.activeSensors + v4.metrics.inactiveSensors),
    total_sites: v4.metrics.totalSites,
    open_tickets: v4.metrics.openTickets,
    critical_tickets: v4.metrics.criticalTickets,
    overdue_invoices: v4.invoices.overdue,
    total_gateways: v4.metrics.totalGateways || 0,
  };

  // Transform current subscription
  // Note: Dashboard endpoint returns limited subscription fields
  const current_subscription: AccountSubscription | undefined = v4.subscription.current
    ? {
        id: v4.subscription.current.id,
        contract_number: v4.subscription.current.subscriptionNumber,
        name: v4.subscription.current.name,
        status: (v4.subscription.current.status as AccountSubscription['status']) || 'active',
        start_date: v4.subscription.current.startDate || '',
        end_date: v4.subscription.current.endDate,
        total_value: parseFloat(v4.subscription.current.totalValue || '0'),
        currency: v4.subscription.current.currency || 'CAD',
        sales_manager_id: v4.subscription.current.salesManagerId || undefined,
        sales_manager_name: v4.subscription.current.salesManagerName || undefined,
        contractual_manager_ids: v4.subscription.current.contractualManagerIds || [],
        contractual_manager_names: v4.subscription.current.contractualManagerNames || [],
        mongo_contract_id: v4.subscription.current.mongoContractId || undefined,
      }
    : undefined;

  // Transform subscription history
  const subscription_history = v4.subscription.history.map((c) => {
    const startYear = new Date(c.startDate).getFullYear();
    const endYear = new Date(c.endDate).getFullYear();
    return {
      year: startYear === endYear ? String(startYear) : `${startYear}-${endYear}`,
      value: parseFloat(c.totalValue || '0'),
      sensors: 0, // Not available in v4 contract data
    };
  });

  // Transform recent activity
  // Note: DAO returns { type, timestamp }, not { activityType, createdAt }
  const recent_activity: AccountActivity[] = v4.recentActivity.map((a) => ({
    id: a.id,
    type: a.type,
    description: a.description,
    timestamp: a.timestamp,
    performed_by: undefined, // Not available from DAO
  }));

  // Transform alerts
  // Note: DAO returns 'error' for critical, map it to 'critical'
  const alerts = v4.alerts.map((a) => ({
    type: a.type,
    message: a.message,
    severity: mapSeverity(a.severity),
  }));

  return {
    account: {
      id: v4.account.id,
      account_number: v4.account.accountNumber,
      name: v4.account.name,
      mongo_organisation_id: v4.account.mongoOrganisationId || undefined,
      organisation_id: v4.account.organisationId || undefined,
      type: v4.account.type as Account['type'],
      status: v4.account.status as Account['status'],
      tier: (v4.account.tier as Account['tier']) || 'standard',
      has_calibration_subscription: ((v4.account.hasCalibrationSubscription || v4.account.hasCalibrationContract || 'false') as Account['has_calibration_subscription']),
      has_calibration_contract: ((v4.account.hasCalibrationSubscription || v4.account.hasCalibrationContract || 'false') as Account['has_calibration_contract']),
      has_onsite_calibration: ((v4.account.hasOnsiteCalibration || 'false') as Account['has_onsite_calibration']),
      calibration_provider: ((v4.account.calibrationProvider || 'other') as Account['calibration_provider']),
      requires_whitelisting: (v4.account.requiresWhitelisting as Account['requires_whitelisting']) || 'false',
      sales_manager_id: v4.account.salesManagerId || null,
      sales_manager_name: v4.account.salesManagerName || null,
      archived_at: v4.account.archivedAt || null,
      archived_by: v4.account.archivedBy || null,
      archived_by_name: v4.account.archivedByName || null,
      first_subscription_date: v4.account.firstSubscriptionDate || v4.account.firstContractDate || undefined,
      first_contract_date: v4.account.firstSubscriptionDate || v4.account.firstContractDate || undefined,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
    health,
    metrics,
    current_subscription,
    current_contract: current_subscription,
    subscription_history,
    contract_history: subscription_history,
    recent_activity,
    alerts,
    has_renewal_opportunity: v4.subscription.hasRenewalOpportunity || false,
    quotes: {
      total: v4.quotes.total || 0,
      preparation: v4.quotes.preparation || 0,
      sent: v4.quotes.sent || 0,
      approved: v4.quotes.approved || 0,
      invoiced: v4.quotes.invoiced || 0,
      preparationValue: v4.quotes.preparationValue || '0',
      sentValue: v4.quotes.sentValue || '0',
      approvedValue: v4.quotes.approvedValue || '0',
      invoicedValue: v4.quotes.invoicedValue || '0',
      totalValue: v4.quotes.totalValue || '0',
    },
    activity_summary: {
      activityCount30Days: v4.activitySummary?.activityCount30Days || 0,
      lastActivityAt: v4.activitySummary?.lastActivityAt || null,
      lastActivityType: v4.activitySummary?.lastActivityType || null,
    },
  };
}

function getHealthFactor(
  score: number | null
): 'good' | 'warning' | 'critical' {
  if (score === null) return 'good';
  if (score >= 70) return 'good';
  if (score >= 40) return 'warning';
  return 'critical';
}

function getPaymentFactor(
  score: number | null
): 'excellent' | 'good' | 'warning' | 'critical' {
  if (score === null) return 'good';
  if (score >= 90) return 'excellent';
  if (score >= 70) return 'good';
  if (score >= 40) return 'warning';
  return 'critical';
}

/**
 * Map DAO severity to frontend severity
 * DAO uses 'error', frontend uses 'critical'
 */
function mapSeverity(severity: string): 'info' | 'warning' | 'critical' {
  if (severity === 'error') return 'critical';
  if (severity === 'warning') return 'warning';
  return 'info';
}

// =============================================================================
// Detail Tab API Functions
// =============================================================================

interface V4SubscriptionsResponse {
  subscriptions: SubscriptionDetail[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

interface V4InvoicesResponse {
  invoices: InvoiceDetail[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

interface V4SitesResponse {
  sites: SiteDetail[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

interface V4ActivitiesResponse {
  activities: ActivityDetail[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

interface V4QuotesResponse {
  quotes: QuoteDetail[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

export async function getAccountSubscriptions(
  accountId: string,
  params?: { pageNumber?: number; pageLength?: number; status?: string }
): Promise<PaginatedResponse<SubscriptionDetail>> {
  const response = await api.get<V4SubscriptionsResponse>(
    `/v4/accounts/${accountId}/subscriptions`,
    params
  );
  return {
    data: response.subscriptions,
    pageNumber: response.pageNumber,
    pageLength: response.pageLength,
    total: response.total,
  };
}

/** @deprecated Use getAccountSubscriptions instead */
export const getAccountContracts = getAccountSubscriptions;

export async function getAccountInvoices(
  accountId: string,
  params?: { pageNumber?: number; pageLength?: number; status?: string; search?: string }
): Promise<PaginatedResponse<InvoiceDetail>> {
  const response = await api.get<V4InvoicesResponse>(
    `/v4/accounts/${accountId}/invoices`,
    params
  );
  return {
    data: response.invoices,
    pageNumber: response.pageNumber,
    pageLength: response.pageLength,
    total: response.total,
  };
}

export async function getAccountSites(
  accountId: string,
  params?: { pageNumber?: number; pageLength?: number }
): Promise<PaginatedResponse<SiteDetail>> {
  const response = await api.get<V4SitesResponse>(
    `/v4/accounts/${accountId}/sites`,
    params
  );
  return {
    data: response.sites,
    pageNumber: response.pageNumber,
    pageLength: response.pageLength,
    total: response.total,
  };
}

/**
 * Every site on an account, following pagination.
 *
 * `getAccountSites` is the paged read behind the Sites TAB, which has real page
 * controls. Pickers are different: they need the WHOLE list, and asking for one
 * page of 100 silently hid the rest from the billing-site dropdown on accounts
 * above that (one real customer has 973 sites). Hard stop so a server returning
 * full pages forever cannot spin the browser.
 */
export async function getAllAccountSites(accountId: string): Promise<SiteDetail[]> {
  const all: SiteDetail[] = [];
  const pageLength = 100;
  const maxPages = 20;

  for (let pageNumber = 0; pageNumber < maxPages; pageNumber += 1) {
    // eslint-disable-next-line no-await-in-loop
    const response = await api.get<V4SitesResponse>(
      `/v4/accounts/${accountId}/sites`,
      { pageNumber, pageLength }
    );
    const page = response.sites || [];
    all.push(...page);
    if (page.length < pageLength) break;
  }

  return all;
}

export async function getAccountActivities(
  accountId: string,
  params?: { pageNumber?: number; pageLength?: number; type?: string }
): Promise<PaginatedResponse<ActivityDetail>> {
  const response = await api.get<V4ActivitiesResponse>(
    `/v4/accounts/${accountId}/activities`,
    params
  );
  return {
    data: response.activities,
    pageNumber: response.pageNumber,
    pageLength: response.pageLength,
    total: response.total,
  };
}

export async function getAccountQuotes(
  accountId: string,
  params?: { pageNumber?: number; pageLength?: number; status?: string; search?: string }
): Promise<PaginatedResponse<QuoteDetail>> {
  const response = await api.get<V4QuotesResponse>(
    `/v4/accounts/${accountId}/quotes`,
    params
  );
  return {
    data: response.quotes,
    pageNumber: response.pageNumber,
    pageLength: response.pageLength,
    total: response.total,
  };
}

// =============================================================================
// V4 Transmitters API (PostgreSQL-backed)
// =============================================================================

interface V4TransmittersResponse {
  transmitters: TransmitterDetail[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

export async function getAccountTransmitters(
  accountId: string,
  params?: {
    pageNumber?: number;
    pageLength?: number;
    search?: string;
    activeOnly?: string;
    enabledStates?: string;
    operationalStatuses?: string;
  }
): Promise<PaginatedResponse<TransmitterDetail>> {
  const response = await api.get<V4TransmittersResponse>(
    `/v4/accounts/${accountId}/transmitters`,
    params
  );
  return {
    data: response.transmitters,
    pageNumber: response.pageNumber,
    pageLength: response.pageLength,
    total: response.total,
  };
}

export async function getAccountGateways(
  accountId: string,
  params?: { pageNumber?: number; pageLength?: number; search?: string; activeOnly?: string }
): Promise<PaginatedResponse<TransmitterDetail>> {
  const response = await api.get<V4TransmittersResponse>(
    `/v4/accounts/${accountId}/transmitters`,
    { ...params, type: 'gateways', includeAwsIotStatus: 'true' }
  );
  return {
    data: response.transmitters,
    pageNumber: response.pageNumber,
    pageLength: response.pageLength,
    total: response.total,
  };
}

// =============================================================================
// V4 Calls API (PostgreSQL-backed, synced from HubSpot)
// =============================================================================

interface V4CallsResponse {
  calls: CallDetail[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

interface V4CommunicationsResponse {
  communications: AccountCommunication[];
  summary: AccountCommunicationsSummary;
  pageNumber: number;
  pageLength: number;
  total: number;
}

export async function getAccountCalls(
  accountId: string,
  params?: { pageNumber?: number; pageLength?: number; outcome?: string; direction?: string }
): Promise<PaginatedResponse<CallDetail>> {
  const response = await api.get<V4CallsResponse>(
    `/v4/accounts/${accountId}/calls`,
    params
  );
  return {
    data: response.calls,
    pageNumber: response.pageNumber,
    pageLength: response.pageLength,
    total: response.total,
  };
}

export async function getAccountCommunications(
  accountId: string,
  params?: {
    pageNumber?: number;
    pageLength?: number;
    channel?: 'all' | 'email' | 'sms' | 'call' | 'alert_sendout';
    status?: 'all' | 'success' | 'failed' | 'pending' | 'acknowledged';
    search?: string;
    from?: string;
    to?: string;
  }
): Promise<PaginatedResponse<AccountCommunication> & { summary: AccountCommunicationsSummary }> {
  const response = await api.get<V4CommunicationsResponse>(
    `/v4/accounts/${accountId}/communications`,
    params
  );
  return {
    data: response.communications,
    summary: response.summary,
    pageNumber: response.pageNumber,
    pageLength: response.pageLength,
    total: response.total,
  };
}

// =============================================================================
// V4 Tickets API (PostgreSQL-backed)
// =============================================================================

/**
 * Get tickets for an account using V4 API (PostgreSQL)
 * Tickets are queried by account ID
 */
export async function getAccountTickets(
  accountId: string,
  params?: { pageNumber?: number; pageLength?: number; status?: string }
): Promise<PaginatedResponse<TicketDetail>> {
  const queryParams: Record<string, string | number> = {
    pageNumber: params?.pageNumber ?? 0,
    pageLength: params?.pageLength ?? 25,
  };

  if (params?.status) {
    queryParams.status = params.status;
  }

  const response = await api.get<{
    tickets: TicketDetail[];
    pageNumber: number;
    pageLength: number;
    total: number;
  }>(`/v4/accounts/${accountId}/tickets`, queryParams);

  return {
    data: response.tickets || [],
    pageNumber: response.pageNumber ?? 0,
    pageLength: response.pageLength ?? 25,
    total: response.total ?? 0,
  };
}

/**
 * Get ticket counts for an account using V4 API (PostgreSQL)
 * Returns open and critical ticket counts
 */
export async function getAccountTicketCounts(
  accountId: string
): Promise<{ open: number; critical: number }> {
  return api.get<{ open: number; critical: number }>(
    `/v4/accounts/${accountId}/tickets/counts`
  );
}

// =============================================================================
// V3 Users API (MongoDB-backed, not migrated to PostgreSQL)
// =============================================================================

// =============================================================================
// V4 Usergroup Members API (PostgreSQL-backed)
// =============================================================================

export interface AccountUsergroup {
  id: string;
  name: string;
  archived: boolean;
  accountId?: string | null;
  accountName?: string | null;
  organisationId?: string | null;
}

interface V4UsergroupsResponse {
  usergroups: AccountUsergroup[];
}

/**
 * Get usergroups for an account (the group entities, not members)
 */
export async function getAccountUsergroups(
  accountId: string
): Promise<AccountUsergroup[]> {
  const response = await api.get<V4UsergroupsResponse>(
    `/v4/accounts/${accountId}/usergroups`
  );
  return response.usergroups || [];
}

/**
 * Get all usergroups across all accounts (for picker dropdowns)
 */
export async function getAllUsergroups(): Promise<AccountUsergroup[]> {
  const response = await api.get<V4UsergroupsResponse>(
    '/v4/accounts/usergroups/all'
  );
  return response.usergroups || [];
}

/**
 * Assign usergroups to an account
 */
export async function assignAccountUsergroups(
  accountId: string,
  usergroupIds: string[]
): Promise<void> {
  await api.put(`/v4/accounts/${accountId}/usergroups`, { usergroupIds });
}

// =============================================================================
// Account Organisation Membership API (V4 PostgreSQL-backed)
// =============================================================================

interface V4AccountOrganisationsResponse {
  organisations: AccountOrganisation[];
}

interface V4AvailableUsergroupsResponse {
  organisations: AvailableUsergroupsOrg[];
}

interface V4AllocateUsergroupResponse {
  previousOwner?: { id: string; accountNumber: string; name: string };
}

/**
 * Get all organisations linked to an account via account_organisations.
 */
export async function getAccountOrganisations(
  accountId: string
): Promise<AccountOrganisation[]> {
  const response = await api.get<V4AccountOrganisationsResponse>(
    `/v4/accounts/${accountId}/organisations`
  );
  return response.organisations ?? [];
}

/**
 * Link an organisation to an account.
 */
export async function addAccountOrganisation(
  accountId: string,
  organisationId: string
): Promise<void> {
  await api.post(`/v4/accounts/${accountId}/organisations`, { organisationId });
}

/**
 * Remove an organisation from an account.
 */
export async function removeAccountOrganisation(
  accountId: string,
  orgId: string
): Promise<void> {
  await api.delete(`/v4/accounts/${accountId}/organisations/${orgId}`);
}

/**
 * Get available usergroups grouped by linked organisation.
 * Each org includes its usergroups with ownership metadata.
 */
export async function getAvailableUsergroups(
  accountId: string
): Promise<AvailableUsergroupsOrg[]> {
  const response = await api.get<V4AvailableUsergroupsResponse>(
    `/v4/accounts/${accountId}/available-usergroups`
  );
  return response.organisations ?? [];
}

/**
 * Allocate (assign ownership of) a usergroup to this account.
 * Returns optional previousOwner when the ug was moved from another account.
 */
export async function allocateUsergroup(
  accountId: string,
  ugId: string
): Promise<V4AllocateUsergroupResponse> {
  return api.post<V4AllocateUsergroupResponse>(
    `/v4/accounts/${accountId}/usergroups/${ugId}/allocate`,
    {}
  );
}

/**
 * Remove ownership of a usergroup from this account.
 */
export async function deallocateUsergroup(
  accountId: string,
  ugId: string
): Promise<void> {
  await api.delete(`/v4/accounts/${accountId}/usergroups/${ugId}`);
}

/**
 * Pin a sensor to this account (override accountId).
 */
export async function pinSensor(
  accountId: string,
  sensorId: string
): Promise<void> {
  await api.post(`/v4/accounts/${accountId}/sensors/${sensorId}/pin`, {});
}

/**
 * Unpin a sensor from this account (remove accountId override).
 */
export async function unpinSensor(
  accountId: string,
  sensorId: string
): Promise<void> {
  await api.delete(`/v4/accounts/${accountId}/sensors/${sensorId}/pin`);
}

interface V4UsergroupMembersResponse {
  members: UsergroupMemberDetail[];
  pageNumber: number;
  pageLength: number;
  total: number;
  distinctTags?: string[];
  distinctRoles?: string[];
}

export interface AccountUsersResponse extends PaginatedResponse<UserDetail> {
  distinctTags: string[];
  distinctRoles: string[];
}

/**
 * Get all account users for an account using V4 API (PostgreSQL)
 * Includes direct organisation memberships in addition to legacy usergroup members.
 */
export async function getAccountUsers(
  accountId: string,
  params?: { pageNumber?: number; pageLength?: number; enabled?: string; tag?: string; role?: string }
): Promise<AccountUsersResponse> {
  const queryParams: Record<string, string | number> = {
    pageNumber: params?.pageNumber ?? 0,
    pageLength: params?.pageLength ?? 10,
  };

  if (params?.enabled) {
    queryParams.enabled = params.enabled;
  }

  if (params?.tag) {
    queryParams.tag = params.tag;
  }

  if (params?.role) {
    queryParams.role = params.role;
  }

  const response = await api.get<V4UsergroupMembersResponse>(
    `/v4/accounts/${accountId}/users`,
    queryParams
  );

  // Transform V4 usergroupMember to UserDetail shape for backward compatibility
  const users: UserDetail[] = (response.members || []).map((m) => ({
    _id: m.mongoUserId,
    // PG UUID — the Users-tab Edit resolves the user through it (useUser →
    // getUserByPgId asserts a UUID). Deliberately NOT m.id: that is the legacy
    // mongo id; feeding it to the UUID lookup was the Edit no-op (R7).
    id: m.pgUserId ?? undefined,
    email: m.email || '',
    firstname: m.firstname,
    lastname: m.lastname,
    phone: m.phone,
    mobile: m.cellular,
    role: m.role,
    tags: m.tags || [],
    login_at: m.loginAt,
    enabled: m.enabled,
    usergroups: (m.usergroups || []).map((ug) => ({ name: ug.name, organisation: '' })),
    sensorgroups: [],
  }));

  return {
    data: users,
    pageNumber: response.pageNumber ?? 0,
    pageLength: response.pageLength ?? 10,
    total: response.total ?? 0,
    distinctTags: response.distinctTags || [],
    distinctRoles: response.distinctRoles || [],
  };
}

// =============================================================================
// V4 Sensors API (PostgreSQL-backed)
// =============================================================================

interface V4SensorFromApi {
  id: string;
  mongoSensorId: string;
  name: string;
  status: number;
  billable: boolean;
  lastReadingAt: string | null;
  currentValue: string | null;
  sensorType: string | null;
  sensorTypeUnit: string | null;
  equipmentType: string | null;
  manufacturer: string | null;
  model: string | null;
  serialNumber: string | null;
  streamName: string | null;
  usergroupName: string | null;
  inputNumber: string | null;
  customId: string | null;
  invoices: string[] | null;
  quotes: string[] | null;
  invoiceAssociations?: { id: string; invoiceNumber: string; status: string; invoiceDate: string | null; total: string }[];
  quoteAssociations?: { id: string; quoteNumber: string; status: string; total: string }[];
  tags: string[] | null;
  archived: boolean;
  maintenance: boolean;
  site: { id: string; name: string } | null;
  location: { id: string; name: string } | null;
  usergroup: { id: string; name: string } | null;
  transmitter: { mongoId: string; name: string | null; devId: string | null } | null;
  organisation: { mongoId: string | null; name: string | null } | null;
  imageUrl: string | null;
  calibrationDue: string | null;
  calibrationCertNumber: string | null;
  mongoCalibrationId: string | null;
  mongoStreamId: string | null;
  mongoInstallationQualificationId: string | null;
  accountIdOverridden: boolean;
  createdAt: string;
  updatedAt: string;
}

interface V4SensorsResponse {
  sensors: V4SensorFromApi[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

export interface GetAccountSensorsParams {
  pageNumber?: number;
  pageLength?: number;
  sortKey?: 'id' | 'name' | 'status' | 'stream' | 'value' | 'timestamp' | 'siteLocation' | 'calibration';
  sortDirection?: 1 | -1;
  status?: string;
  statusKeys?: string;
  hasPhoto?: 'with' | 'without';
  search?: string;
  idSearch?: string;
  nameSearch?: string;
  streamSearch?: string;
  siteLocationSearch?: string;
  valueMin?: string | number;
  valueMax?: string | number;
  lastReadingFrom?: string;
  lastReadingTo?: string;
}

/** Map numeric sensor status to a string label */
function mapSensorStatus(status: number): string {
  const statusMap: Record<number, string> = {
    0: 'unmanaged', 1: 'active', 2: 'warning', 3: 'warning',
    4: 'warning', 5: 'inactive', 6: 'maintenance', 8: 'inhibited', 9: 'archived',
  };
  return statusMap[status] ?? 'unknown';
}

/**
 * Get sensor counts for an account using V4 API (PostgreSQL)
 * Fetches minimal pages with status filter to get totals
 */
export async function getAccountSensorCounts(
  accountId: string
): Promise<{ active: number; inactive: number }> {
  const [activeResponse, inactiveResponse] = await Promise.all([
    api.get<V4SensorsResponse>(`/v4/accounts/${accountId}/sensors`, {
      status: '1',
      pageLength: 1,
      pageNumber: 0,
    }),
    api.get<V4SensorsResponse>(`/v4/accounts/${accountId}/sensors`, {
      status: '5',
      pageLength: 1,
      pageNumber: 0,
    }),
  ]);

  const active = activeResponse.total ?? 0;
  const inactive = inactiveResponse.total ?? 0;

  return {
    active,
    inactive,
  };
}

/**
 * Get sensors for an account using V4 API (PostgreSQL)
 * Transforms V4 response to SensorDetail shape for component compatibility
 */
export async function getAccountSensors(
  accountId: string,
  params?: GetAccountSensorsParams
): Promise<PaginatedResponse<SensorDetail>> {
  const queryParams: Record<string, string | number> = {
    pageNumber: params?.pageNumber ?? 0,
    pageLength: params?.pageLength ?? 25,
    sortKey: params?.sortKey ?? 'name',
    sortDirection: params?.sortDirection ?? 1,
  };

  if (params?.search) {
    queryParams.search = params.search;
  }

  if (params?.statusKeys) {
    queryParams.statusKeys = params.statusKeys;
  }

  if (params?.hasPhoto) {
    queryParams.hasPhoto = params.hasPhoto;
  }

  if (params?.idSearch) {
    queryParams.idSearch = params.idSearch;
  }

  if (params?.nameSearch) {
    queryParams.nameSearch = params.nameSearch;
  }

  if (params?.streamSearch) {
    queryParams.streamSearch = params.streamSearch;
  }

  if (params?.siteLocationSearch) {
    queryParams.siteLocationSearch = params.siteLocationSearch;
  }

  if (params?.valueMin !== undefined && params.valueMin !== '') {
    queryParams.valueMin = params.valueMin;
  }

  if (params?.valueMax !== undefined && params.valueMax !== '') {
    queryParams.valueMax = params.valueMax;
  }

  if (params?.lastReadingFrom) {
    queryParams.lastReadingFrom = params.lastReadingFrom;
  }

  if (params?.lastReadingTo) {
    queryParams.lastReadingTo = params.lastReadingTo;
  }

  // Map legacy status filter to numeric status values.
  // Active = status 1 (Active) + 4 (To Acknowledge)
  // Inactive = status 5 only
  if (params?.status === 'active') {
    queryParams.status = '1,4';
  } else if (params?.status === 'inactive') {
    queryParams.status = '5';
  }

  const response = await api.get<V4SensorsResponse>(
    `/v4/accounts/${accountId}/sensors`,
    queryParams
  );

  // Transform V4 sensor to SensorDetail shape for backward compatibility
  const sensors: SensorDetail[] = (response.sensors || []).map((s) => ({
    _id: s.mongoSensorId,
    id: s.id,
    name: s.name,
    status: mapSensorStatus(s.status),
    stream_name: s.streamName || undefined,
    value: s.currentValue != null ? parseFloat(s.currentValue) : undefined,
    timestamp: s.lastReadingAt || undefined,
    customid: s.customId || undefined,
    sensortype: s.sensorTypeUnit ? { _id: s.sensorType || '', name: '', unit: s.sensorTypeUnit } : undefined,
    site: s.site ? { _id: s.site.id, name: s.site.name } : undefined,
    location: s.location ? { _id: s.location.id, name: s.location.name } : undefined,
    usergroup: s.usergroup ? { _id: s.usergroup.id, name: s.usergroup.name } : undefined,
    transmitter: s.transmitter
      ? {
          _id: s.transmitter.mongoId,
          name: s.transmitter.name || '',
          devId: s.transmitter.devId || null,
        }
      : undefined,
    organisation: s.organisation
      ? { _id: s.organisation.mongoId || '', name: s.organisation.name || '' }
      : undefined,
    image: s.imageUrl ? { _id: '', url: s.imageUrl } : undefined,
    stream: s.calibrationDue || s.calibrationCertNumber || s.mongoStreamId ? {
      _id: s.mongoStreamId || '',
      name: s.streamName || '',
      calibration: {
        cert_number: s.calibrationCertNumber || undefined,
        due: s.calibrationDue || undefined,
        mongoId: s.mongoCalibrationId || undefined,
      },
    } : undefined,
    installationqualification: s.mongoInstallationQualificationId
      ? { _id: s.mongoInstallationQualificationId }
      : undefined,
    accountIdOverridden: s.accountIdOverridden ?? false,
  }));

  return {
    data: sensors,
    pageNumber: response.pageNumber ?? 0,
    pageLength: response.pageLength ?? 25,
    total: response.total ?? 0,
  };
}

// =============================================================================
// V4 Opportunities API (PostgreSQL-backed)
// =============================================================================

/**
 * V4 Opportunity from API (camelCase)
 */
interface V4Opportunity {
  id: string;
  accountId: string;
  mongoUpsellId?: string;
  createdByUserId?: string;
  type: string;
  status: string;
  title: string;
  description?: string;
  estimatedValue?: number;
  probability?: number;
  targetDate?: string;
  hubspotDealId?: string;
  autoDetected: boolean;
  signalType?: string;
  signalData?: Record<string, unknown>;
  createdAt: string;
  updatedAt?: string;
  // HubSpot enrichment fields
  hubspotDealStage?: string;
  hubspotPipeline?: string;
  hubspotOwnerId?: string;
  hubspotCloseDate?: string;
  hubspotIsClosed?: boolean;
  hubspotIsWon?: boolean;
  hubspotAmount?: number;
  hubspotLastSyncedAt?: string;
}

interface V4OpportunitiesResponse {
  opportunities: V4Opportunity[];
  summary: {
    total: number;
    autoDetected: number;
    manual: number;
    totalEstimatedValue: number;
    highPriority: number;
  };
  pageNumber: number;
  pageLength: number;
  total: number;
}

/**
 * Transform V4 opportunity to internal format
 */
function transformV4Opportunity(v4: V4Opportunity): OpportunityDetail {
  // Calculate priority based on probability and estimated value
  let priority: OpportunityDetail['priority'];
  if (v4.probability && v4.estimatedValue) {
    if (v4.probability >= 70 && v4.estimatedValue >= 10000) {
      priority = 'high';
    } else if (v4.probability >= 40 || v4.estimatedValue >= 5000) {
      priority = 'medium';
    } else {
      priority = 'low';
    }
  }

  return {
    id: v4.id,
    title: v4.title,
    type: v4.type as OpportunityDetail['type'],
    status: v4.status as OpportunityDetail['status'],
    description: v4.description,
    estimated_value: v4.estimatedValue,
    probability: v4.probability,
    target_date: v4.targetDate,
    auto_detected: v4.autoDetected,
    signal_type: v4.signalType,
    signal_data: v4.signalData,
    priority,
    created_by: v4.createdByUserId,
    created_at: v4.createdAt,
    updated_at: v4.updatedAt,
    // HubSpot enrichment fields
    hubspot_deal_id: v4.hubspotDealId,
    hubspot_deal_stage: v4.hubspotDealStage,
    hubspot_pipeline: v4.hubspotPipeline,
    hubspot_owner_id: v4.hubspotOwnerId,
    hubspot_close_date: v4.hubspotCloseDate,
    hubspot_is_closed: v4.hubspotIsClosed,
    hubspot_is_won: v4.hubspotIsWon,
    hubspot_amount: v4.hubspotAmount,
    hubspot_last_synced_at: v4.hubspotLastSyncedAt,
  };
}

/**
 * Get opportunities for an account using V4 API (PostgreSQL)
 * Includes both auto-detected signals and manual opportunities
 */
export async function getAccountOpportunities(
  accountId: string,
  params?: { status?: string; stage?: string; includeAutoDetected?: boolean }
): Promise<OpportunitiesResponse> {
  const queryParams: Record<string, string | boolean> = {};

  // Support both 'stage' (new) and 'status' (deprecated) parameters
  if (params?.stage && params.stage !== 'all') {
    queryParams.stage = params.stage;
  } else if (params?.status && params.status !== 'all') {
    queryParams.status = params.status;
  }

  if (params?.includeAutoDetected === false) {
    queryParams.includeAutoDetected = 'false';
  }

  const response = await api.get<V4OpportunitiesResponse>(
    `/v4/accounts/${accountId}/opportunities`,
    queryParams
  );

  return {
    opportunities: response.opportunities.map(transformV4Opportunity),
    summary: {
      total: response.summary.total,
      auto_detected: response.summary.autoDetected,
      manual: response.summary.manual,
      total_estimated_value: response.summary.totalEstimatedValue,
      high_priority: response.summary.highPriority,
    },
    pageNumber: response.pageNumber,
    pageLength: response.pageLength,
    total: response.total,
  };
}

/**
 * Create a new manual opportunity
 */
export async function createAccountOpportunity(
  accountId: string,
  data: CreateOpportunityInput
): Promise<OpportunityDetail> {
  const response = await api.post<V4Opportunity>(
    `/v4/accounts/${accountId}/opportunities`,
    data
  );

  return transformV4Opportunity(response);
}

/**
 * Update an existing opportunity
 */
export async function updateAccountOpportunity(
  accountId: string,
  opportunityId: string,
  data: Partial<CreateOpportunityInput> & { status?: string; stage?: string }
): Promise<OpportunityDetail> {
  const response = await api.put<V4Opportunity>(
    `/v4/accounts/${accountId}/opportunities/${opportunityId}`,
    data
  );

  return transformV4Opportunity(response);
}

/**
 * Delete an opportunity (soft delete)
 */
export async function deleteAccountOpportunity(
  accountId: string,
  opportunityId: string
): Promise<void> {
  await api.delete(
    `/v4/accounts/${accountId}/opportunities/${opportunityId}`
  );
}

// =============================================================================
// V4 Pipeline Summary API (for Overview tab deal insights)
// =============================================================================

/**
 * Get pipeline summary for an account (for Overview tab)
 * Returns aggregated deal metrics, stage breakdown, and next closing deal
 */
export async function getAccountPipelineSummary(
  accountId: string
): Promise<PipelineSummary> {
  return api.get<PipelineSummary>(`/v4/accounts/${accountId}/pipeline-summary`);
}

/**
 * Get deals needing attention for an account (for Overview tab)
 * Returns deals closing soon, stalled deals, and high priority deals
 */
export async function getDealsNeedingAttention(
  accountId: string
): Promise<DealsNeedingAttention> {
  return api.get<DealsNeedingAttention>(`/v4/accounts/${accountId}/deals-needing-attention`);
}

// =============================================================================
// V4 Callback Requests API
// =============================================================================

export interface CallbackRequest {
  id: string;
  projectId: string;
  customerEmail: string;
  customerName: string | null;
  accountManagerId: string | null;
  accountManagerEmail: string | null;
  accountManagerName: string | null;
  status: 'pending' | 'completed' | 'cancelled';
  requestedAt: string;
  completedAt: string | null;
  completedByName: string | null;
  notes: string | null;
  // Joined fields
  projectNumber: string | null;
  projectName: string | null;
  projectType: string | null;
  siteName: string | null;
}

interface V4CallbackRequestsResponse {
  requests: CallbackRequest[];
  total: number;
  pageNumber: number;
  pageLength: number;
}

/**
 * Get callback requests for an account
 * Filters by projects linked to the account
 */
export async function getAccountCallbackRequests(
  accountId: string,
  params?: { status?: string; pageNumber?: number; pageLength?: number }
): Promise<{ requests: CallbackRequest[]; total: number }> {
  const response = await api.get<V4CallbackRequestsResponse>(
    `/v4/callback-requests`,
    { ...params, accountId }
  );
  return {
    requests: response.requests,
    total: response.total,
  };
}

interface V4CallbackRequestResponse {
  success: boolean;
  request: CallbackRequest;
}

/**
 * Mark a callback request as completed
 */
export async function completeCallbackRequest(
  requestId: string,
  notes?: string
): Promise<CallbackRequest> {
  const response = await api.post<V4CallbackRequestResponse>(
    `/v4/callback-requests/${requestId}/complete`,
    { notes }
  );
  return response.request;
}

/**
 * Cancel a callback request
 */
export async function cancelCallbackRequest(
  requestId: string,
  notes?: string
): Promise<CallbackRequest> {
  const response = await api.post<V4CallbackRequestResponse>(
    `/v4/callback-requests/${requestId}/cancel`,
    { notes }
  );
  return response.request;
}

// =============================================================================
// V3 QMS Tickets API (PostgreSQL-backed)
// =============================================================================

interface V3QmsTicketsResponse {
  tickets: QmsTicketSummary[];
  pageNumber: number;
  pageLength: number;
  total?: number;
}

interface V3QmsTicketsCountResponse {
  tickets: [];
  count: number;
}

/**
 * Get QMS tickets for an account using V3 API (PostgreSQL)
 * QMS tickets are queried by account_id (V4 Account UUID)
 *
 * Note: The API doesn't return a total count in normal pagination responses.
 * We estimate based on returned results - if we got a full page, there may be more.
 */
export async function getAccountQmsTickets(
  accountId: string,
  params?: { pageNumber?: number; pageLength?: number; status?: QmsTicketStatus }
): Promise<PaginatedResponse<QmsTicketSummary>> {
  const pageLength = params?.pageLength ?? 10;
  const pageNumber = params?.pageNumber ?? 0;

  const queryParams: Record<string, string | number> = {
    account_id: accountId,
    pageNumber,
    pageLength,
    sortKey: 'created_at',
    sortDirection: -1,
    // Request populated fields for display
    populate: JSON.stringify(['assigned_to']),
  };

  if (params?.status) {
    queryParams.status = params.status;
  }

  const response = await api.get<V3QmsTicketsResponse>('/v4/qmstickets', queryParams);

  const tickets = response.tickets || [];

  // V3 API doesn't return total count in normal pagination responses.
  // Estimate: if we got a full page, assume there's at least one more page.
  // This means we can't show accurate "Page X of Y" but can enable "Next" button properly.
  const hasMore = tickets.length === pageLength;
  const estimatedTotal = hasMore
    ? (pageNumber + 2) * pageLength // At least one more page
    : (pageNumber * pageLength) + tickets.length; // Current page is the last

  return {
    data: tickets,
    pageNumber: response.pageNumber ?? pageNumber,
    pageLength: response.pageLength ?? pageLength,
    total: response.total ?? estimatedTotal,
  };
}

/**
 * Get QMS ticket count for an account using V3 API
 * Returns total count of QMS tickets linked to the account
 */
export async function getAccountQmsTicketCount(accountId: string): Promise<number> {
  const response = await api.get<V3QmsTicketsCountResponse>('/v4/qmstickets', {
    account_id: accountId,
    count: 'true',
  });
  return response.count ?? 0;
}

// =============================================================================
// Entity Reassignment
// =============================================================================

export interface ReassignResult {
  success: boolean;
  id: string;
  previousAccountId: string;
  newAccountId: string;
}

export async function reassignInvoice(invoiceId: string, targetAccountId: string): Promise<ReassignResult> {
  return api.patch<ReassignResult>(`/v4/invoices/${invoiceId}/reassign`, { accountId: targetAccountId });
}

export async function reassignQuote(quoteId: string, targetAccountId: string): Promise<ReassignResult> {
  return api.patch<ReassignResult>(`/v4/quotes/${quoteId}/reassign`, { accountId: targetAccountId });
}

export async function reassignContract(subscriptionId: string, targetAccountId: string): Promise<ReassignResult> {
  return api.patch<ReassignResult>(`/v4/subscriptions/${subscriptionId}/reassign`, { accountId: targetAccountId });
}

export async function reassignSensor(sensorId: string, targetAccountId: string): Promise<ReassignResult> {
  return api.patch<ReassignResult>(`/v4/sensors/${sensorId}/reassign`, { accountId: targetAccountId });
}

export async function reassignTransmitter(transmitterId: string, targetAccountId: string): Promise<ReassignResult> {
  return api.patch<ReassignResult>(`/v4/transmitters/${transmitterId}/reassign`, { accountId: targetAccountId });
}

// =============================================================================
// Transmitter Connections Export
// =============================================================================

/**
 * Export transmitter connections as CSV
 * Returns connection type info (Gateway, WiFi, LTE) for all transmitters in an organisation
 *
 * @param accountId - Account ID
 * @param activeOnly - Only include active transmitters
 * @returns CSV file as a Blob for download
 */
/**
 * Refresh PG sensors for an account by pulling fresh value/timestamp/status
 * from MongoDB (the customer-app source of truth). Bypasses CDC drift.
 * Returns the number of sensor rows updated.
 */
export async function refreshAccountSensorsFromMongo(
  accountId: string
): Promise<{ updated: number; total: number }> {
  return api.post<{ updated: number; total: number }>(
    `/v4/accounts/${accountId}/sensors/refresh-from-mongo`,
    {}
  );
}

/**
 * Lazy-load sync for the Users tab. Pulls usergroups + users + memberships
 * from MongoDB into PG and reconciles drift. Replaces continuous CDC for
 * these collections.
 */
export interface SyncUsersFromMongoResult {
  usergroups: { attached: number; metadataRefreshed: number };
  users: { scanned: number; upserted: number };
  members: { upserted: number; removed: number };
}

export async function syncAccountUsersFromMongo(
  accountId: string
): Promise<SyncUsersFromMongoResult> {
  return api.post<SyncUsersFromMongoResult>(
    `/v4/accounts/${accountId}/sync-users-from-mongo`,
    {}
  );
}

export async function exportTransmitterConnections(
  accountId: string,
  activeOnly = false
): Promise<Blob> {
  const params = new URLSearchParams({
    organisationId: accountId,
    format: 'csv',
  });

  if (activeOnly) {
    params.set('activeOnly', 'true');
  }

  const url = `/api/v4/transmitter-connections/export?${params.toString()}`;

  // Use fetch directly for blob response (with /api prefix for Vite proxy)
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Export failed: ${response.statusText}`);
  }

  return response.blob();
}

// =============================================================================
// Account Document Upload (Drive + DMS unified)
// =============================================================================

export interface AccountDocumentUploadResponse {
  driveFileId: string;
  driveFileName: string;
  driveFileLink?: string;
  dmsDocumentId: string;
}

export async function uploadAccountDocument(
  accountId: string,
  file: File,
  opts?: {
    entityType?: 'quote' | 'invoice' | 'subscription';
    entityId?: string;
    documentType?: string;
  },
): Promise<AccountDocumentUploadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  if (opts?.entityType) formData.append('entity_type', opts.entityType);
  if (opts?.entityId) formData.append('entity_id', opts.entityId);
  if (opts?.documentType) formData.append('document_type', opts.documentType);
  return api.upload<AccountDocumentUploadResponse>(
    `/v4/accounts/${accountId}/upload-document`,
    formData,
  );
}

// =============================================================================
// Account Notes
// =============================================================================

export async function getAccountNotes(
  accountId: string,
  params?: { pageNumber?: number; pageLength?: number; category?: string }
): Promise<{ notes: AccountNote[]; pageNumber: number; pageLength: number; total: number }> {
  return api.get<{ notes: AccountNote[]; pageNumber: number; pageLength: number; total: number }>(
    `/v4/accounts/${accountId}/notes`,
    params
  );
}

export async function createAccountNote(
  accountId: string,
  data: { content: string; category?: string }
): Promise<{ note: AccountNote }> {
  return api.post<{ note: AccountNote }>(`/v4/accounts/${accountId}/notes`, data);
}

export async function updateAccountNote(
  accountId: string,
  noteId: string,
  data: { content?: string; category?: string }
): Promise<{ note: AccountNote }> {
  return api.put<{ note: AccountNote }>(`/v4/accounts/${accountId}/notes/${noteId}`, data);
}

export async function deleteAccountNote(
  accountId: string,
  noteId: string
): Promise<void> {
  await api.delete(`/v4/accounts/${accountId}/notes/${noteId}`);
}

// =============================================================================
// Importable Entities (V4 API — backend resolves org internally)
// =============================================================================

export interface ImportableQuote {
  _id: string;
  quoteNumber: string | null;
  projectName: string | null;
  name: string | null;
  status: string;
  total: string;
  currency: string;
  createdDate: string | null;
  organisationId: string | null;
  alreadyImported: boolean;
}

export async function getImportableQuotes(accountId: string): Promise<ImportableQuote[]> {
  const response = await api.get<{ quotes: ImportableQuote[] }>(
    `/v4/accounts/${accountId}/importable-quotes`
  );
  return response.quotes || [];
}

export async function importQuotesToAccount(
  accountId: string,
  mongoQuoteIds: string[]
): Promise<{ imported: number; reassigned: number; errors: string[] }> {
  return api.post<{ imported: number; reassigned: number; errors: string[] }>(
    `/v4/accounts/${accountId}/import-quotes`,
    { mongoQuoteIds }
  );
}

// -----------------------------------------------------------------------------

export interface ImportableInvoice {
  _id: string;
  invoiceNumber: string | null;
  status: string;
  total: string;
  currency: string;
  invoiceDate: string | null;
  projectName: string | null;
  organisationId: string | null;
  alreadyImported: boolean;
}

export async function getImportableInvoices(accountId: string): Promise<ImportableInvoice[]> {
  const response = await api.get<{ invoices: ImportableInvoice[] }>(
    `/v4/accounts/${accountId}/importable-invoices`
  );
  return response.invoices || [];
}

export async function importInvoicesToAccount(
  accountId: string,
  mongoInvoiceIds: string[]
): Promise<{ imported: number; reassigned: number; errors: string[] }> {
  return api.post<{ imported: number; reassigned: number; errors: string[] }>(
    `/v4/accounts/${accountId}/import-invoices`,
    { mongoInvoiceIds }
  );
}

// -----------------------------------------------------------------------------

export interface ImportableTransmitter {
  _id: string;
  devId: string;
  name: string | null;
  deviceType: string | null;
  activeFlag: boolean;
  lastpacketAt: string | null;
  organisationId: string | null;
  alreadyImported: boolean;
}

export async function getImportableTransmitters(accountId: string): Promise<ImportableTransmitter[]> {
  const response = await api.get<{ transmitters: ImportableTransmitter[] }>(
    `/v4/accounts/${accountId}/importable-transmitters`
  );
  return response.transmitters || [];
}

export async function importTransmittersToAccount(
  accountId: string,
  mongoTransmitterIds: string[]
): Promise<{ imported: number; reassigned: number; errors: string[] }> {
  return api.post<{ imported: number; reassigned: number; errors: string[] }>(
    `/v4/accounts/${accountId}/import-transmitters`,
    { mongoTransmitterIds }
  );
}

// -----------------------------------------------------------------------------

export interface ImportableSensor {
  _id: string;
  name: string;
  status: number;
  streamName: string | null;
  value: number | null;
  organisationId: string | null;
  alreadyImported: boolean;
}

export async function getImportableSensors(accountId: string): Promise<ImportableSensor[]> {
  const response = await api.get<{ sensors: ImportableSensor[] }>(
    `/v4/accounts/${accountId}/importable-sensors`
  );
  return response.sensors || [];
}

export async function importSensorsToAccount(
  accountId: string,
  mongoSensorIds: string[]
): Promise<{ imported: number; reassigned: number; errors: string[] }> {
  return api.post<{ imported: number; reassigned: number; errors: string[] }>(
    `/v4/accounts/${accountId}/import-sensors`,
    { mongoSensorIds }
  );
}

/**
 * Get full V4 quote detail for editing (PostgreSQL-backed)
 */
export async function getQuoteV4ForEdit(pgQuoteId: string): Promise<V4QuoteFormDetail> {
  return api.get<V4QuoteFormDetail>(`/v4/quotes/${pgQuoteId}`);
}
